import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { billingApi } from "@/api/client";

export type DisputeStatus =
  | "open"
  | "in_review"
  | "resolved_uphold"
  | "resolved_recalc"
  | "withdrawn";

export interface BillingDispute {
  id: string;
  billing_period_id: string;
  tenant_id: string;
  reason: string;
  disputed_event_ids: string[] | null;
  contact_email: string;
  status: DisputeStatus;
  resolution_note: string | null;
  resolved_by_admin_id: string | null;
  created_at: string | null;
  resolved_at: string | null;
}

export interface BillingContact {
  tenant_id: string;
  billing_email: string;
}

export interface FileDisputePayload {
  reason: string;
  disputed_event_ids?: string[];
  contact_email: string;
}

const DISPUTES_KEY = ["billing-disputes"];

export function useBillingDisputes(tenantId: string) {
  return useQuery({
    queryKey: [...DISPUTES_KEY, tenantId],
    queryFn: () =>
      billingApi
        .get("disputes")
        .json<{ tenant_id: string; disputes: BillingDispute[] }>(),
    enabled: !!tenantId,
  });
}

export function useBillingContact(tenantId: string) {
  return useQuery({
    queryKey: ["billing-contact", tenantId],
    queryFn: () => billingApi.get("contact").json<BillingContact>(),
    enabled: !!tenantId,
  });
}

export function useFileDispute(periodId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: FileDisputePayload) =>
      billingApi
        .post(`periods/${periodId}/disputes`, { json: payload })
        .json<BillingDispute>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DISPUTES_KEY });
      qc.invalidateQueries({ queryKey: ["billing-v2-period-detail", periodId] });
    },
  });
}
